import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/landing/Navbar";
import { Footer } from "@/components/landing/Footer";
import { useNavigate } from "react-router-dom";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function absolutePhotoUrl(path) {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${API_BASE}${path}`;
}

export default function AboutUs() {
  const navigate = useNavigate();
  const [founders, setFounders] = useState({
    founder_john_photo_url: "",
    founder_chris_photo_url: "",
  });

  useEffect(() => {
    api
      .get("/site/about")
      .then((res) => setFounders(res.data || {}))
      .catch(() => {});
  }, []);

  const handleCta = () => navigate("/#enquiry");

  return (
    <div className="min-h-screen bg-brand-cream text-brand-navy" data-testid="about-us-page">
      <Navbar onCta={handleCta} />

      <main className="pt-8 md:pt-14 pb-20">
        {/* Intro */}
        <section className="max-w-4xl mx-auto px-6 md:px-10 pt-6 pb-14 md:pb-20">
          <p className="text-[11px] md:text-[13px] uppercase tracking-[0.45em] text-brand-orange font-semibold">About Us</p>
          <h1
            className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight tracking-tight text-brand-navy"
            data-testid="about-heading"
          >
            Making Compliance Work Better for Businesses
          </h1>
          <div className="mt-8 space-y-5 text-base md:text-lg leading-relaxed text-brand-slate">
            <p>
              Vanguard Beacon was born from a simple observation: businesses spend significant time managing
              compliance, yet many of the processes behind it remain manual, fragmented and disconnected from
              day-to-day operations.
            </p>
            <p>
              We believe compliance should not be a burden that businesses have to manage around their operations.
              It should be built into the way they work.
            </p>
            <p>
              Our mission is to help businesses simplify compliance through technology — turning complex
              requirements into practical, intuitive workflows that make it easier for teams to capture the right
              information, stay accountable and remain audit-ready.
            </p>
          </div>
        </section>

        {/* Founders */}
        <section className="max-w-6xl mx-auto px-6 md:px-10" data-testid="about-founders">
          <div className="flex items-baseline gap-3 mb-10">
            <p className="text-[11px] md:text-[13px] uppercase tracking-[0.45em] text-brand-orange font-semibold">
              Our Founders
            </p>
            <div className="flex-1 border-t border-brand-navy/10" />
          </div>

          <div className="grid md:grid-cols-2 gap-8 md:gap-10">
            <FounderCard
              name="John Ong"
              role="Co-Founder"
              photoUrl={absolutePhotoUrl(founders.founder_john_photo_url)}
              testid="founder-john"
              bio={[
                "With a background spanning aerospace and agritech, John has spent his career focused on operational excellence, process optimisation and finding better ways to make businesses run more efficiently. His experience building and operating Spore Gardens led him to experience first-hand the administrative burden of food safety compliance and inspired the development of Vanguard Beacon.",
                "John brings an operational perspective to Vanguard Beacon — looking beyond compliance documentation to understand how technology can improve the way work is actually performed.",
              ]}
            />
            <FounderCard
              name="Chris Chen"
              role="Co-Founder"
              photoUrl={absolutePhotoUrl(founders.founder_chris_photo_url)}
              testid="founder-chris"
              bio={[
                "Chris is a Singapore-based entrepreneur and consultant with extensive experience supporting SMEs and businesses in their growth and transformation. His work in food safety consultancy has included supporting businesses with ISO 22000, HACCP, GMP and FSSC requirements.",
                "Chris brings deep practical knowledge of food safety management and certification, ensuring that Vanguard Beacon is built around the realities of how businesses manage compliance.",
              ]}
            />
          </div>
        </section>

        {/* Beliefs */}
        <section className="max-w-4xl mx-auto px-6 md:px-10 mt-20 md:mt-28" data-testid="about-beliefs">
          <div className="flex items-baseline gap-3 mb-10">
            <p className="text-[11px] md:text-[13px] uppercase tracking-[0.45em] text-brand-orange font-semibold">
              What We Believe
            </p>
            <div className="flex-1 border-t border-brand-navy/10" />
          </div>

          <ul className="space-y-5">
            {[
              "We believe technology should make compliance simpler, not more complicated.",
              "We believe employees should spend less time maintaining paperwork and more time doing meaningful work.",
              "And we believe the best compliance systems are not simply digital versions of paper forms — they are intelligent workflows that help businesses operate better.",
            ].map((line, i) => (
              <li
                key={i}
                className="flex items-start gap-4 text-base md:text-lg leading-relaxed text-brand-slate"
                data-testid={`about-belief-${i + 1}`}
              >
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-orange" />
                <span>{line}</span>
              </li>
            ))}
          </ul>

          <p className="mt-10 text-base md:text-lg leading-relaxed text-brand-navy font-medium">
            Vanguard Beacon exists to bridge the gap between compliance and operations, helping businesses move
            from manual documentation to smarter, more connected ways of working.
          </p>

          <div className="mt-12 flex flex-wrap items-center gap-4">
            <button
              onClick={handleCta}
              className="inline-flex items-center justify-center rounded-full bg-brand-orange text-white px-7 py-3 text-[11px] uppercase tracking-[0.25em] font-semibold hover:bg-brand-orange-hover transition-colors"
              data-testid="about-cta-book-review"
            >
              Book a compliance review
            </button>
            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center justify-center rounded-full border border-brand-navy/20 text-brand-navy px-7 py-3 text-[11px] uppercase tracking-[0.25em] font-semibold hover:bg-white transition-colors"
              data-testid="about-back-home"
            >
              Back to homepage
            </button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

function FounderCard({ name, role, photoUrl, bio, testid }) {
  return (
    <article
      className="bg-white border border-brand-navy/10 rounded-3xl p-6 md:p-8 flex flex-col"
      data-testid={testid}
    >
      <div className="relative w-full aspect-[4/5] rounded-2xl overflow-hidden bg-brand-cream mb-6 border border-brand-navy/5">
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={`${name} — ${role}, Vanguard Beacon`}
            className="w-full h-full object-cover"
            data-testid={`${testid}-photo`}
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center text-[10px] uppercase tracking-[0.3em] text-brand-slate/60 text-center px-4"
            data-testid={`${testid}-photo-placeholder`}
          >
            Photo coming soon
          </div>
        )}
      </div>
      <p className="text-[10px] uppercase tracking-[0.4em] text-brand-orange font-semibold">{role}</p>
      <h3 className="mt-1 text-2xl md:text-3xl font-semibold text-brand-navy" data-testid={`${testid}-name`}>
        {name}
      </h3>
      <div className="mt-5 space-y-4 text-sm md:text-base leading-relaxed text-brand-slate">
        {bio.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>
    </article>
  );
}
